// Documentation.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './LegalPages.css';
import logo from '../images/logo.png';

function Documentation() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('getting-started');

  const user = JSON.parse(localStorage.getItem('user'));
  const user_id = user?.user_id;

  const sections = [
    { id: 'getting-started', title: 'Getting Started', icon: 'bi-rocket' },
    { id: 'chat-features', title: 'Chat Features', icon: 'bi-chat' },
    { id: 'image-analysis', title: 'Image Analysis', icon: 'bi-image' },
    { id: 'code-assistant', title: 'Code Assistant', icon: 'bi-code-slash' },
    { id: 'account-management', title: 'Account Management', icon: 'bi-person' },
    { id: 'faq', title: 'FAQ', icon: 'bi-question-circle' }
  ];

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="legal-page light-theme">
      {/* Navigation */}
      <nav className="legal-navbar">
        <div className="legal-nav-container">
          <button
            className="legal-brand"
            onClick={() => navigate(user_id ? '/home' : '/')}
          >
            <img src={logo} alt='Marshall AI Logo' className='legal-logo' />
            <span>Marshall AI</span>
          </button>
          <button
            className="legal-back-btn"
            onClick={() => navigate(-1)}
          >
            <i className="bi bi-arrow-left me-2"></i>
            Back
          </button>
        </div>
      </nav>

      {/* Main Content with Sidebar */}
      <div className="docs-container">
        {/* Sidebar Navigation */}
        <aside className="docs-sidebar">
          <h3>Documentation</h3>
          <ul className="docs-nav">
            {sections.map((section) => (
              <li key={section.id}>
                <button
                  className={`docs-nav-btn ${activeSection === section.id ? 'active' : ''}`}
                  onClick={() => scrollToSection(section.id)}
                >
                  <i className={`bi ${section.icon} me-2`}></i>
                  {section.title}
                </button>
              </li>
            ))}
          </ul>
        </aside>

        {/* Content Area */}
        <div className="docs-content">
          <h1 className="legal-title" style={{ textAlign: 'left', marginBottom: '2rem' }}>
            Documentation
          </h1>

          {/* Getting Started */}
          <section id="getting-started" className="docs-section">
            <h2>
              <i className="bi bi-rocket me-2"></i>
              Getting Started
            </h2>
            <p>Welcome to Marshall AI! This guide will help you get started with our AI assistant.</p>
            
            <h3>Creating an Account</h3>
            <p>To start using Marshall AI:</p>
            <ol>
              <li>Click the "Sign Up" button on the homepage</li>
              <li>Enter your email and create a password</li>
              <li>Or sign in quickly with Google OAuth</li>
              <li>Verify your email address</li>
              <li>Complete your profile setup</li>
            </ol>

            <h3>Your First Conversation</h3>
            <p>Once logged in, you can start chatting immediately:</p>
            <ol>
              <li>Click "New Chat" in the sidebar</li>
              <li>Type your question or request in the input field</li>
              <li>Press Enter or click the send button</li>
              <li>Wait for the AI to respond</li>
            </ol>

            <div className="info-box">
              <i className="bi bi-info-circle"></i>
              <p><strong>Pro Tip:</strong> You have 20 free credits daily. Each conversation uses one credit.</p>
            </div>
          </section>

          {/* Chat Features */}
          <section id="chat-features" className="docs-section">
            <h2>
              <i className="bi bi-chat me-2"></i>
              Chat Features
            </h2>
            
            <h3>Basic Chat</h3>
            <p>Simply type your message and press Enter. The AI understands natural language and context.</p>

            <h3>Message Formatting</h3>
            <p>You can use markdown-style formatting in your messages:</p>
            <ul>
              <li><code>**bold**</code> - Makes text bold</li>
              <li><code>*italic*</code> - Makes text italic</li>
              <li><code>`code`</code> - Inline code</li>
              <li><code>```language```</code> - Code blocks with syntax highlighting</li>
            </ul>

            <h3>Chat Management</h3>
            <ul>
              <li><strong>Rename Chat:</strong> Click the three dots menu and select "Rename"</li>
              <li><strong>Share Chat:</strong> Copy entire conversation to clipboard</li>
              <li><strong>Delete Chat:</strong> Remove unwanted conversations</li>
              <li><strong>Search:</strong> Find specific chats using the search feature</li>
            </ul>

            <h3>Keyboard Shortcuts</h3>
            <table className="shortcuts-table">
              <thead>
                <tr>
                  <th>Action</th>
                  <th>Shortcut</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Send message</td>
                  <td><kbd className='text-dark'>Enter</kbd></td>
                </tr>
                <tr>
                  <td>New line</td>
                  <td><kbd className='text-dark'>Shift</kbd> + <kbd className='text-dark'>Enter</kbd></td>
                </tr>
                <tr>
                  <td>Stop generation</td>
                  <td>Click Stop button</td>
                </tr>
                <tr>
                  <td>New chat</td>
                  <td>Click "New Chat" button</td>
                </tr>
              </tbody>
            </table>
          </section>

          {/* Image Analysis */}
          <section id="image-analysis" className="docs-section">
            <h2>
              <i className="bi bi-image me-2"></i>
              Image Analysis
            </h2>
            
            <h3>Uploading Images</h3>
            <p>Marshall AI can analyze images you upload:</p>
            <ol>
              <li>Click the image icon in the input area</li>
              <li>Select up to 3 images (JPEG, PNG, WEBP)</li>
              <li>Add a description or question about the images</li>
              <li>Send your message</li>
            </ol>

            <h3>Supported Formats</h3>
            <ul>
              <li>JPEG / JPG</li>
              <li>PNG</li>
              <li>WEBP</li>
            </ul>

            <h3>Image Analysis Capabilities</h3>
            <ul>
              <li>Object detection and identification</li>
              <li>Text extraction (OCR)</li>
              <li>Scene description</li>
              <li>Color analysis</li>
              <li>Content moderation</li>
            </ul>

            <div className="warning-box">
              <i className="bi bi-exclamation-triangle"></i>
              <p><strong>Note:</strong> Maximum file size is 5MB per image. Maximum 3 images per message.</p>
            </div>
          </section>

          {/* Code Assistant */}
          <section id="code-assistant" className="docs-section">
            <h2>
              <i className="bi bi-code-slash me-2"></i>
              Code Assistant
            </h2>
            
            <h3>Supported Languages</h3>
            <p>Marshall AI supports 30+ programming languages including:</p>
            <div className="lang-grid">
              <span className="lang-tag">Python</span>
              <span className="lang-tag">JavaScript</span>
              <span className="lang-tag">TypeScript</span>
              <span className="lang-tag">Java</span>
              <span className="lang-tag">C++</span>
              <span className="lang-tag">C#</span>
              <span className="lang-tag">Go</span>
              <span className="lang-tag">Rust</span>
              <span className="lang-tag">PHP</span>
              <span className="lang-tag">Ruby</span>
              <span className="lang-tag">Swift</span>
              <span className="lang-tag">Kotlin</span>
            </div>

            <h3>Code Features</h3>
            <ul>
              <li><strong>Syntax Highlighting:</strong> Automatic language detection</li>
              <li><strong>Copy Code:</strong> One-click copy to clipboard</li>
              <li><strong>Download:</strong> Save code as file with proper extension</li>
              <li><strong>Debug Help:</strong> Ask for bug fixes and explanations</li>
              <li><strong>Code Review:</strong> Get suggestions for improvement</li>
            </ul>

            <h3>Example Queries</h3>
            <div className="example-box">
              <p>"Write a Python function to sort a list"</p>
              <p>"Explain this JavaScript code"</p>
              <p>"Debug my React component"</p>
              <p>"Convert this Python code to Java"</p>
            </div>
          </section>

          {/* Account Management */}
          <section id="account-management" className="docs-section">
            <h2>
              <i className="bi bi-person me-2"></i>
              Account Management
            </h2>
            
            <h3>Profile Settings</h3>
            <p>Access your profile settings from the dropdown menu:</p>
            <ul>
              <li><strong>Profile Picture:</strong> Upload or remove your avatar</li>
              <li><strong>Username:</strong> Your display name</li>
              <li><strong>Email:</strong> Your registered email address</li>
              <li><strong>Login Type:</strong> Google or Email/Password</li>
            </ul>

            <h3>Theme Settings</h3>
            <p>Choose your preferred theme:</p>
            <ul>
              <li><strong>Light:</strong> Clean, bright interface</li>
              <li><strong>Dark:</strong> Easy on eyes in low light</li>
              <li><strong>System:</strong> Follows your OS preference</li>
            </ul>

            <h3>Credits System</h3>
            <ul>
              <li>Free accounts get 20 credits daily</li>
              <li>Each conversation uses 1 credit</li>
              <li>Credits reset at midnight UTC</li>
              <li>Check remaining credits in the header</li>
            </ul>
          </section>

          {/* FAQ */}
          <section id="faq" className="docs-section">
            <h2>
              <i className="bi bi-question-circle me-2"></i>
              Frequently Asked Questions
            </h2>
            
            <div className="docs-faq">
              <div className="faq-item">
                <h3>Is Marshall AI free?</h3>
                <p>Yes! We offer 20 free credits daily. Premium plans coming soon.</p>
              </div>
              <div className="faq-item">
                <h3>Can I delete my account?</h3>
                <p>Yes, you can request account deletion from Profile Settings.</p>
              </div>
              <div className="faq-item">
                <h3>Is my data secure?</h3>
                <p>All conversations are encrypted. We never sell your data.</p>
              </div>
              <div className="faq-item">
                <h3>What AI model do you use?</h3>
                <p>We use advanced language models optimized for conversation.</p>
              </div>
              <div className="faq-item">
                <h3>Can I use it on mobile?</h3>
                <p>Yes! Marshall AI is fully responsive and works on all devices.</p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default Documentation;